/**
 * Browser-side key store. Each signed-in user has one ristretto255 group keypair; secrets
 * (the metadata secret, a confidant's share) are sealed to its public half by others and
 * opened here. Stored in localStorage for now; hardening with the passkey PRF extension is on
 * the roadmap (Milestone 7).
 */
import { bytes, generateGroupKeypair, openSecretWith, type GroupKeypair } from "@tether/crypto";

const PREFIX = "tether.groupKey.";
const BACKUP_PREFIX = "tether.backupConfirmed.";

/** Self-attested: the user told us they saved the backup somewhere; we cannot verify it. */
export function isBackupConfirmed(userId: string): boolean {
  try {
    return localStorage.getItem(BACKUP_PREFIX + userId) === "1";
  } catch {
    return false;
  }
}
export function markBackupConfirmed(userId: string): void {
  try {
    localStorage.setItem(BACKUP_PREFIX + userId, "1");
  } catch {
    /* storage unavailable: the prompt will simply reappear */
  }
}

export function loadGroupKey(userId: string): GroupKeypair | null {
  try {
    const raw = localStorage.getItem(PREFIX + userId);
    if (!raw) return null;
    const j = JSON.parse(raw) as { secretKey: string; publicKey: string };
    return {
      secretKey: bytes.fromBase64Url(j.secretKey),
      publicKey: bytes.fromBase64Url(j.publicKey),
    };
  } catch {
    return null;
  }
}

export function getOrCreateGroupKey(userId: string): GroupKeypair {
  const existing = loadGroupKey(userId);
  if (existing) return existing;
  const kp = generateGroupKeypair();
  localStorage.setItem(
    PREFIX + userId,
    JSON.stringify({
      secretKey: bytes.toBase64Url(kp.secretKey),
      publicKey: bytes.toBase64Url(kp.publicKey),
    }),
  );
  return kp;
}

export function openSealed(kp: GroupKeypair, sealedB64u: string, label: string): Uint8Array {
  return openSecretWith(kp.secretKey, kp.publicKey, bytes.fromBase64Url(sealedB64u), label);
}

/** Export for backup (the user is warned in the UI). */
export function exportGroupKey(userId: string): string | null {
  return localStorage.getItem(PREFIX + userId);
}
export function importGroupKey(userId: string, json: string): void {
  const j = JSON.parse(json) as { secretKey: string; publicKey: string };
  bytes.fromBase64Url(j.secretKey);
  bytes.fromBase64Url(j.publicKey);
  localStorage.setItem(PREFIX + userId, JSON.stringify(j));
  markBackupConfirmed(userId);
}
